import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import { 
  Users, 
  Search, 
  Filter, 
  RefreshCw, 
  Loader2, 
  FileText, 
  Globe, 
  LogOut, 
  Settings,
  Eye,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  MoreHorizontal
} from 'lucide-react';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });
  const [groups, setGroups] = useState([]);

  const handleLogout = () => {
    logout();
  };

  const fetchUsers = async (page = 1, search = searchTerm, filterParams = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.items_per_page,
        ...filterParams
      };
      
      if (search) {
        params.search = search;
      }

      const response = await adminService.getUsers(params);
      
      setUsers(response.users || []);
      setPagination(response.pagination || {
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        items_per_page: 20,
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await adminService.getGroups();
      console.log('Groups response:', response);
      const groupsData = response.groups || [];
      console.log('Setting groups:', groupsData);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
             // Fallback to default groups if API fails
       const fallbackGroups = [
         { id: 1, name: 'FREE', display_name: 'Free' },
         { id: 2, name: 'PRO', display_name: 'Pro' },
         { id: 3, name: 'ENTERPRISE', display_name: 'Enterprise' }
       ];
      console.log('Setting fallback groups:', fallbackGroups);
      setGroups(fallbackGroups);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1, searchTerm, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchUsers(1, searchTerm, newFilters);
  };

  const handlePageChange = (page) => {
    fetchUsers(page, searchTerm, filters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers(pagination.current_page, searchTerm, filters);
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await adminService.updateUserRole(userId, !isAdmin);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, is_admin: !isAdmin } : user
        )
      );
      
      toast.success(`User ${isAdmin ? 'removed from' : 'promoted to'} admin role`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await adminService.updateUserStatus(userId, newStatus);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );
      
      toast.success(`User ${currentStatus === 'active' ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleChangeGroup = async (userId, newGroupId) => {
    try {
      await adminService.updateUserGroup(userId, newGroupId);
      
             setUsers(prevUsers => 
         prevUsers.map(user => 
           user.id === userId ? { 
             ...user, 
             group_id: newGroupId,
             group_name: groups.find(g => g.id === parseInt(newGroupId))?.name || '',
             group_display_name: groups.find(g => g.id === parseInt(newGroupId))?.display_name || ''
           } : user
         )
       );
      
      toast.success(`User group updated successfully`);
    } catch (error) {
      console.error('Error updating user group:', error);
      toast.error('Failed to update user group');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
              </Link>
            </div>
                         <div className="flex items-center space-x-4">
               <Link to="/dashboard" className="btn-secondary flex items-center">
                 <FileText className="h-4 w-4 mr-2" />
                 Dashboard
               </Link>
               <Link to="/public" className="btn-secondary flex items-center">
                 <Globe className="h-4 w-4 mr-2" />
                 Public
               </Link>
               <button
                 onClick={handleLogout}
                 className="btn-secondary flex items-center"
               >
                 <LogOut className="h-4 w-4 mr-2" />
                 Logout
               </button>
             </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
              <p className="text-gray-600">Manage users, view profiles, and control access</p>
            </div>
            <Link to="/admin" className="btn-secondary flex items-center">
              <Settings className="h-4 w-4 mr-2" />
              Back to Admin
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </form>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="admin">Admins</option>
                <option value="user">Users</option>
              </select>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-600">
            Showing {users.length} of {pagination.total_items || users.length} users
          </p>
          {pagination.total_pages > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Page {pagination.current_page} of {pagination.total_pages}</span>
            </div>
          )}
        </div>

                                                                       {/* Users Table */}
           <div className="bg-white rounded-lg shadow-sm border border-gray-200">
             <div>
             <table className="w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                 <tr>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                     User
                   </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                     Role
                   </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                     Status
                   </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                     Items
                   </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                     Joined
                   </th>
                                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Group
                    </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                     Actions
                   </th>
                 </tr>
               </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                                 {users.map((user) => (
                   <tr key={user.id} className="hover:bg-gray-50">
                     <td className="px-4 py-4">
                       <div className="flex items-center">
                         <div className="flex-shrink-0 h-10 w-10">
                           <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                             <Users className="h-5 w-5 text-gray-600" />
                           </div>
                         </div>
                         <div className="ml-4 min-w-0 flex-1">
                           <div className="text-sm font-medium text-gray-900 truncate">{user.full_name}</div>
                           <div className="text-sm text-gray-500 truncate">{user.email}</div>
                         </div>
                       </div>
                     </td>
                     <td className="px-4 py-4 whitespace-nowrap">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                         user.is_admin 
                           ? 'bg-red-100 text-red-800' 
                           : 'bg-gray-100 text-gray-800'
                       }`}>
                         {user.is_admin ? 'Admin' : 'User'}
                       </span>
                     </td>
                     <td className="px-4 py-4 whitespace-nowrap">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                         user.status === 'active' 
                           ? 'bg-green-100 text-green-800' 
                           : 'bg-red-100 text-red-800'
                       }`}>
                         {user.status === 'active' ? 'Active' : 'Inactive'}
                       </span>
                     </td>
                     <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                       {user.item_count}
                     </td>
                     <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                       {formatDate(user.created_at)}
                     </td>
                                           <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <select
                          value={user.group_id || (groups.length > 0 ? groups[0].id : '')}
                          onChange={(e) => handleChangeGroup(user.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {groups.length === 0 && <option value="">Loading groups... ({groups.length})</option>}
                                                     {groups.map((group) => (
                             <option key={group.id} value={group.id}>
                               {group.display_name || group.name}
                             </option>
                           ))}
                        </select>
                      </td>
                                                                 <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="relative group">
                          <button className="inline-flex items-center p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          
                          {/* Dropdown menu */}
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                {user.is_admin ? <UserX className="h-4 w-4 mr-2 text-red-500" /> : <Shield className="h-4 w-4 mr-2 text-blue-500" />}
                                {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                              </button>
                              <button
                                onClick={() => handleToggleStatus(user.id, user.status)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                {user.status === 'active' ? <UserX className="h-4 w-4 mr-2 text-red-500" /> : <UserCheck className="h-4 w-4 mr-2 text-green-500" />}
                                {user.status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 border rounded-lg ${
                    page === pagination.current_page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 